import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween"; // Import the isBetween plugin
import useApiService from "../../hooks/useApiService";
import { IFacility, IShiftTimes, IClinicianProfile } from "../../interfaces/types";
import ShiftModal from "../../components/shiftDetailPage";
import { useNotification, useOne, BaseRecord } from "@refinedev/core";
import { colors, semanticColors } from "../../theme";
import { getDisplayStatus } from "../../shared/utils/get-status-color";
import { ThreeDots } from 'react-loader-spinner';

// Extend dayjs with the isBetween plugin
dayjs.extend(isBetween);

import {
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  NoSymbolIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  UserIcon,
  ClipboardDocumentCheckIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline';
import StatCard from "../dashboard/stat-card";

interface HttpErrorType {
  statusCode: number;
  message: string;
}

function isHttpError(error: unknown): error is HttpErrorType {
  return typeof error === "object" && error !== null && "statusCode" in error && "message" in error;
}

// New interfaces for the updated API
interface IClinicianShift {
  id: string;
  shift_id: string;
  clinician_id: string | null;
  firstname?: string;
  lastname?: string;
  email: unknown;
  photo?: string | null;
  status: string;
  date: string;
  start_time: string;
  end_time: string;
  shift_start_time: string;
  shift_end_time: string;
  base_rate: number;
  rate_margin: number;
  description?: string;
  position_id: string;
  position_name: string;
  immutable_clock_in?: string | null;
  immutable_clock_out?: string | null;
}

interface IShiftsResponse {
  stats: {
    completed: number;
    confirmed: number;
    cancelled: number;
    open: number;
    selected?: number;
    inProgress?: number;
  };
  clinicianShifts: IClinicianShift[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    limit: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

interface VhUserPeople {
  firstname: string;
  lastname: string;
  email?: string;
  phone?: string;
}

interface VhUser {
  id?: string | number;
  people: VhUserPeople;
}

interface MeDataWrapper extends BaseRecord {
  id: string | number;
  data: [IFacility, VhUser];
}

export const ShiftList: React.FC = () => {
  const api = useApiService();
  const { open: openNotification } = useNotification();

  const [currentFacility, setCurrentFacility] = useState<IFacility | null>(null);
  const { data: meData, isLoading: isLoadingMe, error: meError } = useOne<MeDataWrapper>({
    resource: "people",
    id: "me",
  });

  const [clinicianShifts, setClinicianShifts] = useState<IClinicianShift[]>([]);
  const [shiftsLoading, setShiftsLoading] = useState<boolean>(true);
  const [shiftsApiError, setShiftsApiError] = useState<string | null>(null);
  const [shiftsStats, setShiftsStats] = useState<IShiftsResponse['stats'] | null>(null);
  const [pagination, setPagination] = useState<IShiftsResponse['pagination'] | null>(null);

  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const [selectedClinicianShift, setSelectedClinicianShift] = useState<IClinicianShift | undefined>(undefined);
  const [clinicianProfile, setClinicianProfile] = useState<IClinicianProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState<boolean>(false);
  const [profileError, setProfileError] = useState<string | null>(null);


  // Set default date range to the full current week (Monday to Sunday)
  const getCurrentWeekRange = () => {
    const today = dayjs();
    
    // Calculate Monday of current week manually to avoid dayjs week calculation issues
    const todayDay = today.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const daysFromMonday = todayDay === 0 ? 6 : todayDay - 1; // Days to subtract to get to Monday
    const startOfWeek = today.subtract(daysFromMonday, 'day').startOf('day');
    
    // Cover the full week: Monday start through Sunday end
    const startDate = startOfWeek.toDate();
    const endDate = startOfWeek.add(6, 'day').endOf('day').toDate();
    
    return [startDate, endDate] as [Date, Date];
  };

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(getCurrentWeekRange());
  const [startDate, endDate] = dateRange;
  
  // Track which preset is currently selected
  const [selectedPreset, setSelectedPreset] = useState<'today' | 'thisWeek' | 'last7Days' | 'thisMonth' | null>('thisWeek');
  
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [isPageLoading, setIsPageLoading] = useState<boolean>(false);
  const [isRowsPerPageLoading, setIsRowsPerPageLoading] = useState<boolean>(false);
  const [hasInitialLoad, setHasInitialLoad] = useState<boolean>(false);
  const [dateSelectionLoading, setDateSelectionLoading] = useState<boolean>(false);
  const apiCallInProgress = useRef<boolean>(false);

  const fetchClinicianShiftsCallback = useCallback(
    async (facilityId: string, showPageLoading = false) => {
      if (!facilityId) {
        setClinicianShifts([]);
        setShiftsLoading(false);
        setIsPageLoading(false);
        if (!shiftsApiError) {
          setShiftsApiError("Cannot fetch shifts: Facility ID is missing.");
        }
        return;
      }
      try {
        if (showPageLoading) {
          setIsPageLoading(true);
        } else {
          setShiftsLoading(true);
        }
        setShiftsApiError(null);
        const dates = startDate && endDate ? [dayjs(startDate).format("YYYY-MM-DD"), dayjs(endDate).format("YYYY-MM-DD")] : [];
        
        const requestBody = {
          dates,
          limit: rowsPerPage,
          page: currentPage
        };
        
        const response = await api.post<IShiftsResponse>(
          `/shifts/clinicians-shifts?facilityId=${facilityId}`,
          requestBody,
        );
        
        const processedShifts: IClinicianShift[] = response.data.clinicianShifts.map((shift: IClinicianShift) => {
          const isPastDate = dayjs(shift.shift_start_time).isBefore(dayjs(), "day");
          let determinedStatus: string = shift.status;
          if (
            isPastDate &&
            (shift.status === "ACTIVE" || shift.status === "CONFIRMED" || shift.status === "SELECTED") &&
            !shift.clinician_id
          ) {
            determinedStatus = "EXPIRED";
          }
          return {
            ...shift,
            status: determinedStatus,
          };
        });
        
        setClinicianShifts(processedShifts);
        setShiftsStats(response.data.stats);
        setPagination(response.data.pagination);
      } catch (error: unknown) {
        let errorMessage = "Error fetching shifts list.";
        if (isHttpError(error)) errorMessage = error.message;
        else if (error instanceof Error) errorMessage = error.message;
        setShiftsApiError(errorMessage);
        setClinicianShifts([]);
        if (typeof openNotification === "function") {
          openNotification({
            type: "error",
            description: "Error Fetching Shifts",
            message: errorMessage,
          });
        }
      } finally {
        setShiftsLoading(false);
        setIsPageLoading(false);
        setIsRowsPerPageLoading(false);
        apiCallInProgress.current = false;
      }
    },
    [api, openNotification, startDate, endDate, rowsPerPage, currentPage],
  );

  // Single effect to handle all API calls and state management
  useEffect(() => {
    // Skip if still loading
    if (isLoadingMe) {
      return;
    }
    

    
    // Handle facility data loading
    if (meData?.data && Array.isArray(meData.data) && meData.data.length > 0) {
      const facilityData = meData.data[0] as IFacility;
      if (facilityData && typeof facilityData === "object" && "facility_id" in facilityData) {
        // Only update if facility ID is different to prevent unnecessary re-renders
        if (currentFacility?.facility_id !== facilityData.facility_id) {
          setCurrentFacility(facilityData);
        }
        setShiftsApiError(null);
      } else {
        setCurrentFacility(null);
        setShiftsApiError("Facility data not found for your account.");
        setShiftsLoading(false);
        return;
      }
    } else if (!isLoadingMe && !meError) {
      setCurrentFacility(null);
      setShiftsApiError("Could not retrieve your facility information.");
      setShiftsLoading(false);
      return;
    } else if (meError) {
      setCurrentFacility(null);
      setShiftsApiError(`Error fetching your information: ${meError.message}`);
      setShiftsLoading(false);
      return;
    }
    
    // Handle API calls - only when we have all required data
    if (currentFacility?.facility_id && startDate && endDate && !apiCallInProgress.current) {
      if (!hasInitialLoad) {
        // Initial load
        setHasInitialLoad(true);
        apiCallInProgress.current = true;
        fetchClinicianShiftsCallback(currentFacility.facility_id);
      } else {
        // Subsequent calls (date changes, pagination) - only if not already loading
        if (!shiftsLoading && !isPageLoading) {
          apiCallInProgress.current = true;
          fetchClinicianShiftsCallback(currentFacility.facility_id, currentPage > 1 || rowsPerPage !== 10);
        }
      }
    }
  }, [meData, isLoadingMe, meError, currentFacility?.facility_id, startDate, endDate, hasInitialLoad, currentPage, rowsPerPage, fetchClinicianShiftsCallback]);



  const fetchClinicianProfile = useCallback(
    async (clinicianId: string) => {
      try {
        setProfileLoading(true);
        setProfileError(null);
        const response = await api.get<IClinicianProfile>(`/clinicians/clinician-profile?clinicianId=${clinicianId}&isFacilityUser=true`);
        setClinicianProfile(response.data);
      } catch (error) {
        setProfileError("Failed to load clinician profile.");
        setClinicianProfile(null);
      } finally {
        setProfileLoading(false);
      }
    },
    [api],
  );

  const handleDetailsClick = useCallback(
    async (shift: IClinicianShift) => {
      setSelectedClinicianShift(shift);
      if (shift.clinician_id) {
        await fetchClinicianProfile(shift.clinician_id);
      } else {
        setClinicianProfile(null);
      }
      setModalOpen(true);
    },
    [fetchClinicianProfile],
  );

  const onUpdateShiftInList = (id: string, shiftTime: IShiftTimes) => {
    setClinicianShifts((prevShifts) =>
      prevShifts.map((shift) =>
        shift.shift_id === id
          ? {
              ...shift,
              immutable_clock_in: shiftTime.reported_clock_in ?? shift.immutable_clock_in,
              immutable_clock_out: shiftTime.reported_clock_out ?? shift.immutable_clock_out,
            }
          : shift,
      ),
    );
  };

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    
    // Ensure proper date order: start should be before end
    if (start && end && start > end) {
      setDateRange([end, start]);
    } else {
      setDateRange([start, end]);
    }
    
    setCurrentPage(1); // Reset to first page when date range changes
    
    // Only show loading state when both dates are selected (complete range)
    if (start && end) {
      setDateSelectionLoading(true);
      // Clear loading state after a short delay to show the loading animation
      setTimeout(() => {
        setDateSelectionLoading(false);
      }, 1000);
    } else {
      // No loading state when only one date is selected
      setDateSelectionLoading(false);
    }
    
    // Clear preset selection when dates are manually changed
    // This will be set back to a preset if handlePresetSelection is called
    setSelectedPreset(null);
    
    // The useEffect will automatically trigger the API call when dates change
  };

  // Function to handle preset selection with proper highlighting
  const handlePresetSelection = (preset: 'today' | 'thisWeek' | 'last7Days' | 'thisMonth') => {
    setSelectedPreset(preset);
    
    // Show loading state for preset selection
    setDateSelectionLoading(true);
    
    switch (preset) {
      case 'today': {
        const today = new Date();
        setDateRange([today, today]);
        setCurrentPage(1);
        break;
      }
      case 'thisWeek': {
        const currentWeek = getCurrentWeekRange();
        setDateRange(currentWeek);
        setCurrentPage(1);
        break;
      }
      case 'last7Days': {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 6); // Subtract 6 to get 7 days total (inclusive)
        setDateRange([start, end]);
        setCurrentPage(1);
        break;
      }
      case 'thisMonth': {
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        setDateRange([monthStart, monthEnd]);
        setCurrentPage(1);
        break;
      }
    }
    
    // Clear loading state after a short delay
    setTimeout(() => {
      setDateSelectionLoading(false);
    }, 1000);
  };

  // Fixed refresh function to properly reset to week-to-date
  const handleRefresh = () => {
    if (currentFacility?.facility_id) {
      // Reset to current week's full range
      const currentWeek = getCurrentWeekRange();
      setDateRange(currentWeek);
      setSelectedPreset('thisWeek'); // Reset to default preset
      setCurrentPage(1);
      // Reset the API call flag and trigger API call directly
      apiCallInProgress.current = false;
      fetchClinicianShiftsCallback(currentFacility.facility_id);
    }
  };

  // Function to handle page change with loading state
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    // The useEffect will automatically trigger the API call
  };

  // Function to handle rows per page change
  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setIsRowsPerPageLoading(true);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page
    // The useEffect will automatically trigger the API call
  };

  const handleExport = () => {
    if (!currentFacility?.facility_id) {
      openNotification?.({
        type: "error",
        description: "Error Exporting Shifts",
        message: "Facility ID is missing.",
      });
      return;
    }
    
    if (!startDate || !endDate) {
      openNotification?.({
        type: "error",
        description: "Error Exporting Shifts",
        message: "Date range is not selected.",
      });
      return;
    }

    const link = document.createElement("a");
    const startDateStr = dayjs(startDate).format('YYYY-MM-DD');
    const endDateStr = dayjs(endDate).format('YYYY-MM-DD');
    
    // Use the new export endpoint with facilityId and dates parameters
    link.href = `${import.meta.env.VITE_API_URL}/shifts/export-facility-clinicians-shifts?facilityId=${currentFacility.facility_id}&dates=${startDateStr},${endDateStr}`;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalPages = useMemo(() => {
    return pagination?.totalPages || 1;
  }, [pagination]);

  const paginatedShifts = useMemo(() => {
    return clinicianShifts;
  }, [clinicianShifts]);

  const stats = useMemo(() => {
    if (shiftsStats) {
      return {
        open: shiftsStats.open,
        confirmed: shiftsStats.confirmed,
        completed: shiftsStats.completed,
        cancelled: shiftsStats.cancelled,
        selected: shiftsStats.selected ?? clinicianShifts.filter((s) => s.status.toUpperCase() === "SELECTED").length,
        inProgress: shiftsStats.inProgress ?? clinicianShifts.filter((s) => s.status.toUpperCase() === "CLOCKED_IN").length,
      };
    }
    // Fallback to local calculation if stats not available
    const open = clinicianShifts.filter((s) => s.status.toUpperCase() === "ACTIVE").length;
    const confirmed = clinicianShifts.filter((s) => s.status.toUpperCase() === "CONFIRMED").length;
    const completed = clinicianShifts.filter((s) => s.status.toUpperCase() === "COMPLETED").length;
    const cancelled = clinicianShifts.filter((s) => s.status.toUpperCase() === "CANCELLED").length;
    const selected = clinicianShifts.filter((s) => s.status.toUpperCase() === "SELECTED").length;
    const inProgress = clinicianShifts.filter((s) => s.status.toUpperCase() === "CLOCKED_IN").length;
    return { open, confirmed, completed, cancelled, selected, inProgress };
  }, [clinicianShifts, shiftsStats]);



  const formatDate = (dateStr: string): string => {
    return dayjs(dateStr).format("MMM DD, YYYY");
  };

  const formatTime = (start: string, end: string): string => {
    const startTime = dayjs(start);
    const endTime = dayjs(end);
    if (!startTime.isValid() || !endTime.isValid()) return "Invalid Time";
    return `${startTime.format("h:mm A")} - ${endTime.format("h:mm A")}`;
  };

  const getStatusStyle = (status: string) => {
    const displayStatus = getDisplayStatus(status);
    
    // Return the same styling approach as shift calendar
    return {
      backgroundColor: displayStatus === 'Confirmed' ? '#E0EDE2' : 
                     displayStatus === 'Open' ? '#E0E8ED' : 
                     displayStatus === 'Cancelled' ? '#FFEAEA' : 
                     displayStatus === 'Selected' ? '#FFF8E6' : 
                     displayStatus === 'Worked' ? '#E0EDE2' :
                     displayStatus === 'Expired' ? '#FFF8E6' :
                     displayStatus === 'No Show' ? '#FFEAEA' :
                     displayStatus === 'Unresolved' ? '#FFE4CC' : 
                     displayStatus === 'Applicants Applied' ? '#E0E8ED' :
                     displayStatus === 'In Progress' ? '#E0EDE2' : '#F3F4F6',
      color: displayStatus === 'Confirmed' ? '#3A7D44' : 
             displayStatus === 'Open' ? '#3A5F7D' : 
             displayStatus === 'Cancelled' ? '#C84648' : 
             displayStatus === 'Selected' ? '#D4A123' : 
             displayStatus === 'Worked' ? '#3A7D44' :
             displayStatus === 'Expired' ? '#D4A123' :
             displayStatus === 'No Show' ? '#C84648' :
             displayStatus === 'Unresolved' ? '#E67E22' : 
             displayStatus === 'Applicants Applied' ? '#3A5F7D' :
             displayStatus === 'In Progress' ? '#3A7D44' : '#6B7280'
    };
  };

  const pageIsLoading = isLoadingMe || (shiftsLoading && clinicianShifts.length === 0 && !shiftsApiError && !meError);

if (pageIsLoading) {
  return (
    <div className="flex min-h-screen items-center justify-center p-4 max-[768px]:p-3 max-[640px]:p-2" style={{ backgroundColor: semanticColors.background.primary }}>
      <div className="flex flex-col items-center space-y-4">
        <img src="/logo-dark-mono.png" alt="VitalHive Logo" style={{ height: "150px" }} />
        <ThreeDots
          visible={true}
          height="60"
          width="60"
          color="#fbb03b"
          radius="9"
          ariaLabel="three-dots-loading"
          wrapperStyle={{}}
          wrapperClass=""
        />
        
      </div>
    </div>
  );
}

// Also replace the loading state inside the table (around line 945) with this:
{shiftsLoading && clinicianShifts.length === 0 && !shiftsApiError && (
  <div className="flex min-h-[200px] items-center justify-center p-6 text-center max-[768px]:min-h-[200px] max-[768px]:p-5 max-[640px]:min-h-[150px] max-[640px]:p-4 sm:min-h-[300px]">
    <div className="flex flex-col items-center space-y-3">
      <ThreeDots
        visible={true}
        height="50"
        width="50"
        color="#fbb03b"
        radius="9"
        ariaLabel="three-dots-loading"
        wrapperStyle={{}}
        wrapperClass=""
      />
      
    </div>
  </div>
)}

// Also update the date selection loading state (around line 860):
{(shiftsLoading || dateSelectionLoading) && (
  <div className="mt-4 animate-in fade-in-0 slide-in-from-bottom-2 duration-300">
    <div className="flex items-center justify-center rounded-xl p-4" style={{ backgroundColor: semanticColors.background.tertiary }}>
      <div className="flex items-center space-x-3">
        <ThreeDots
          visible={true}
          height="30"
          width="30"
          color="#fbb03b"
          radius="9"
          ariaLabel="three-dots-loading"
          wrapperStyle={{}}
          wrapperClass=""
        />
        <span className="text-sm font-medium" style={{ color: semanticColors.text.primary }}>
          {dateSelectionLoading ? 'Processing date selection...' : 'Loading shifts for selected date range...'}
        </span>
      </div>
    </div>
  </div>
)}
  return (
    <>
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: semanticColors.background.primary }}>
        {/* Header - Compact Desktop */}
        <div className="relative overflow-hidden rounded-xl" style={{ background: semanticColors.background.gradient }}>
          <div className="absolute inset-0 bg-black/10 rounded-xl"></div>
          <div className="relative p-4 sm:p-6 lg:px-6 lg:py-4">
            <div className="mx-auto max-w-6xl">
              <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center sm:gap-4">
                <div>
                  <h1 
                    className="text-2xl font-bold sm:text-3xl lg:text-2xl"
                    style={{
                      background: 'linear-gradient(to right, rgb(245, 185, 87), rgb(255, 237, 205), rgb(171, 109, 10))',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      fontFamily: 'Inter, sans-serif'
                    }}
                  >
                    Facility Shifts
                  </h1>
                  <p className="mt-1 text-sm text-white/90 sm:text-base lg:text-sm">
                    Manage shifts for {currentFacility?.facility_name || "your facility"}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleExport}
                    disabled={!currentFacility?.facility_id || clinicianShifts.length === 0}
                    className="flex size-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm transition-all duration-200 hover:bg-white/30 disabled:cursor-not-allowed disabled:opacity-50 sm:size-11 lg:size-9"
                    title="Export shifts data"
                  >
                    <ArrowDownTrayIcon className="size-5 text-white sm:size-6 lg:size-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <main className="flex-1">
          <div className="mx-auto max-w-6xl p-4 sm:p-6 lg:px-6 lg:py-4">
            {/* Enhanced Stats Cards Section */}
            <div className="mb-6 rounded-2xl p-4 sm:p-6 relative overflow-hidden" style={{ 
              background: `linear-gradient(135deg, ${semanticColors.background.primary} 0%, ${semanticColors.background.secondary} 100%)`,
              border: `1px solid ${semanticColors.border.light}`,
              boxShadow: `0 4px 20px ${semanticColors.card.shadow}`
            }}>
              {/* Decorative Background Elements */}
              <div className="absolute top-0 right-0 w-32 h-32 opacity-5" style={{
                background: `radial-gradient(circle, ${semanticColors.status.info} 0%, transparent 70%)`
              }} />
              <div className="absolute bottom-0 left-0 w-24 h-24 opacity-5" style={{
                background: `radial-gradient(circle, ${semanticColors.status.success} 0%, transparent 70%)`
              }} />
              
              {/* Subtle Grid Pattern */}
              <div className="absolute inset-0 opacity-5" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23${semanticColors.status.info.replace('#', '')}' fill-opacity='0.1'%3E%3Cpath d='M0 0h1v40H0V0zm39 0h1v40h-1V0zM0 0h40v1H0V0zm0 39h40v1H0v-1z'/%3E%3C/g%3E%3C/svg%3E")`
              }} />
              
              <div className="mb-5 relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="h-8 w-1 rounded-full" style={{ backgroundColor: semanticColors.status.info }} />
                      <h2 className="text-xl font-bold tracking-tight" style={{ color: semanticColors.text.primary }}>
                        Shift Overview
                      </h2>
                    </div>
                    <p className="text-sm ml-4" style={{ color: semanticColors.text.tertiary }}>
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-6 lg:gap-6 relative z-10 items-stretch">
                <div className="animate-in fade-in-0 slide-in-from-bottom-2 duration-700 delay-100 h-full">
                  <StatCard
                    title="OPEN SHIFTS"
                    value={stats.open}
                    Icon={ClockIcon}
                    color="#D4A123"
                    iconColor="#D4A123"
                    borderColor="#D4A123"
                  />
                </div>
                <div className="animate-in fade-in-0 slide-in-from-bottom-2 duration-700 delay-200 h-full">
                  <StatCard
                    title="CONFIRMED SHIFTS"
                    value={stats.confirmed}
                    Icon={CheckBadgeIcon}
                    color="#3A7D44"
                    iconColor="#3A7D44"
                    borderColor="#3A7D44"
                  />
                </div>
                <div className="animate-in fade-in-0 slide-in-from-bottom-2 duration-700 delay-250 h-full">
                  <StatCard
                    title="SELECTED SHIFTS"
                    value={stats.selected}
                    Icon={ClipboardDocumentCheckIcon}
                    color="#D4A123"
                    iconColor="#D4A123"
                    borderColor="#D4A123"
                  />
                </div>
                <div className="animate-in fade-in-0 slide-in-from-bottom-2 duration-700 delay-300 h-full">
                  <StatCard
                    title="COMPLETED SHIFTS"
                    value={stats.completed}
                    Icon={UserIcon}
                    color="#4A6FA5"
                    iconColor="#4A6FA5"
                    borderColor="#4A6FA5"
                  />
                </div>
                <div className="animate-in fade-in-0 slide-in-from-bottom-2 duration-700 delay-400 h-full">
                  <StatCard
                    title="CANCELLED SHIFTS"
                    value={stats.cancelled}
                    Icon={NoSymbolIcon}
                    color="#C84648"
                    iconColor="#C84648"
                    borderColor="#C84648"
                  />
                </div>
                <div className="animate-in fade-in-0 slide-in-from-bottom-2 duration-700 delay-450 h-full">
                  <StatCard
                    title="IN PROGRESS"
                    value={stats.inProgress}
                    Icon={ArrowPathIcon}
                    color="#3A7D44"
                    iconColor="#3A7D44"
                    borderColor="#3A7D44"
                  />
                </div>
              </div>
              
              {/* Bottom Accent Line */}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 opacity-20" style={{
                background: `linear-gradient(90deg, transparent 0%, ${semanticColors.status.info} 50%, transparent 100%)`
              }} />
            </div>

            {/* Enhanced Date Range Selector */}
            <div className="mb-6 rounded-2xl border p-4 sm:p-5 lg:p-4 relative" style={{ 
              backgroundColor: semanticColors.card.background, 
              borderColor: semanticColors.card.border,
              background: `linear-gradient(135deg, ${semanticColors.background.primary} 0%, ${semanticColors.background.secondary} 100%)`
            }}>

              {/* Decorative Background Elements */}
              <div className="absolute inset-0 rounded-2xl opacity-5" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23${semanticColors.status.info.replace('#', '')}' fill-opacity='0.1'%3E%3Cpath d='M0 0h1v60H0V0zm59 0h1v60h-1V0zM0 0h60v1H0V0zm0 59h60v1H0v-1z'/%3E%3C/g%3E%3C/svg%3E")`
              }} />
              
              {/* Custom CSS for Beautiful Calendar */}
              <style dangerouslySetInnerHTML={{
                __html: `
                  .custom-range-calendar {
                    background: white !important;
                    border-radius: 20px !important;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
                    border: 1px solid #e5e7eb !important;
                    padding: 20px !important;
                    font-family: 'Inter', sans-serif !important;
                  }
                  .custom-range-calendar .react-datepicker__header {
                    background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%) !important;
                    border-bottom: 2px solid #d1d5db !important;
                    border-radius: 16px 16px 0 0 !important;
                    padding: 16px 0 !important;
                  }
                  .custom-range-calendar .react-datepicker__current-month {
                    font-size: 18px !important;
                    font-weight: 700 !important;
                    color: #1f2937 !important;
                    margin-bottom: 8px !important;
                  }
                  .custom-range-calendar .react-datepicker__day-name {
                    color: #6b7280 !important;
                    font-weight: 600 !important;
                    font-size: 14px !important;
                    width: 40px !important;
                    height: 40px !important;
                    line-height: 40px !important;
                    margin: 2px !important;
                  }
                  .custom-range-calendar .react-datepicker__day {
                    width: 40px !important;
                    font-weight: 500 !important;
                    font-size: 14px !important;
                    transition: all 0.2s ease !important;
                  }
                  .custom-range-calendar .react-datepicker__day:hover {
                    background-color: rgba(245, 158, 11, 0.1) !important;
                    color: #f59e0b !important;
                    transform: scale(1.05) !important;
                  }
                  .custom-range-calendar .react-datepicker__day--selected {
                    background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%) !important;
                    color: white !important;
                    font-weight: 600 !important;
                    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3) !important;
                  }
                  .custom-range-calendar .react-datepicker__day--in-range {
                    background: linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(251, 191, 36, 0.15) 100%) !important;
                    color: #f59e0b !important;
                    font-weight: 600 !important;
                  }
                  .custom-range-calendar .react-datepicker__day--keyboard-selected {
                    background: rgba(245, 158, 11, 0.2) !important;
                    color: #f59e0b !important;
                  }
                  .custom-range-calendar .react-datepicker__navigation {
                    top: 20px !important;
                  }
                  .custom-range-calendar .react-datepicker__navigation-icon {
                    width: 8px !important;
                    height: 8px !important;
                    border-width: 2px 2px 0 0 !important;
                    border-color: #6b7280 !important;
                  }
                  .custom-range-calendar .react-datepicker__navigation-icon:hover {
                    border-color: #f59e0b !important;
                  }
                  .custom-range-calendar .react-datepicker__month-container {
                    background: transparent !important;
                  }
                  
                  /* Remove any background effects from the input field */
                  input[type="text"] {
                    background: transparent !important;
                    box-shadow: none !important;
                  }
                  .react-datepicker__input-container input {
                    background: transparent !important;
                    box-shadow: none !important;
                    background-color: transparent !important;
                  }
                  .react-datepicker__input-container {
                    background: transparent !important;
                    background-color: transparent !important;
                  }
                  
                  /* Override any default DatePicker styles */
                  .react-datepicker-wrapper {
                    background: transparent !important;
                    background-color: transparent !important;
                  }
                  
                  /* Ensure clean text rendering */
                  .react-datepicker__input-container input {
                    text-shadow: none !important;
                    -webkit-text-stroke: 0 !important;
                    font-smooth: always !important;
                    -webkit-font-smoothing: antialiased !important;
                    -moz-osx-font-smoothing: grayscale !important;
                    text-overflow: visible !important;
                    overflow: visible !important;
                    white-space: nowrap !important;
                  }
                  
                                     /* Ensure the input field can display full date range */
                   .react-datepicker__input-container {
                     width: 100% !important;
                   }
                   
                   .react-datepicker__input-container input {
                     width: 100% !important;
                     min-width: 0 !important;
                     box-sizing: border-box !important;
                   }
                   
                   /* Ensure date range text is fully visible */
                   .react-datepicker__input-container input[type="text"] {
                     padding-right: 10rem !important;
                     text-overflow: clip !important;
                     overflow: visible !important;
                   }
                   
                   /* Custom input styling for date range */
                   .react-datepicker__input-container input.custom-date-input {
                     font-family: 'Inter', sans-serif !important;
                     font-size: 16px !important;
                     line-height: 1.5 !important;
                     color: #1f2937 !important;
                     background-color: white !important;
                     border: 2px solid #e5e7eb !important;
                     border-radius: 16px !important;
                     padding: 16px 160px 16px 20px !important;
                     width: 100% !important;
                     box-sizing: border-box !important;
                     text-overflow: visible !important;
                     overflow: visible !important;
                     white-space: nowrap !important;
                     letter-spacing: 0.025em !important;
                   }
                   
                   .react-datepicker__input-container input.custom-date-input:focus {
                     border-color: #f59e0b !important;
                     box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.2) !important;
                     outline: none !important;
                   }
                   
                   /* Fix overlapping text issues */
                   .react-datepicker__input-container input.custom-date-input {
                     position: relative !important;
                     z-index: 1 !important;
                   }
                   
                   /* Ensure no text duplication or ghosting */
                   .react-datepicker__input-container input.custom-date-input::before,
                   .react-datepicker__input-container input.custom-date-input::after {
                     display: none !important;
                   }
                   
                   /* Remove any default react-datepicker styling that might cause overlap */
                   .react-datepicker__input-container input.custom-date-input {
                     background-image: none !important;
                     background-repeat: no-repeat !important;
                     background-position: center !important;
                   }
                   
                   /* Fix for text cutoff in date range input */
                   .react-datepicker__input-container input {
                     text-overflow: visible !important;
                     overflow: visible !important;
                     white-space: nowrap !important;
                     padding-right: 20px !important;
                     min-width: 400px !important;
                   }
                   
                   /* Ensure full text visibility for date range */
                   .react-datepicker__input-container {
                     overflow: visible !important;
                     min-width: 400px !important;
                   }
                   
                   /* Override any conflicting padding */
                   .react-datepicker__input-container input[type="text"] {
                     padding-right: 20px !important;
                     text-overflow: visible !important;
                     overflow: visible !important;
                     white-space: nowrap !important;
                     min-width: 400px !important;
                   }
                   
                   /* Force text to be fully visible */
                   .react-datepicker__input-container input {
                     width: auto !important;
                     min-width: 400px !important;
                     max-width: none !important;
                   }
                `
              }} />
              
              <div className="relative z-10">
                {/* Header Section */}
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex size-10 items-center justify-center rounded-xl" style={{ backgroundColor: semanticColors.status.info + '20' }}>
                      <ClockIcon className="size-5" style={{ color: semanticColors.status.info }} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold" style={{ color: semanticColors.text.primary }}>
                        Filter Shifts
                      </h3>
                      <p className="text-sm" style={{ color: semanticColors.text.secondary }}>
                        Select a date range to view specific shifts
                      </p>
                    </div>
                  </div>
                  
                  {/* Quick Actions */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleRefresh}
                      disabled={shiftsLoading && clinicianShifts.length > 0}
                      className={`group relative flex size-10 items-center justify-center rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60 sm:size-11 ${
                        !selectedPreset ? 'ring-2 ring-amber-300 ring-offset-1' : ''
                      }`}
                      style={{ 
                        backgroundColor: !selectedPreset ? semanticColors.status.warning + '20' : semanticColors.background.tertiary,
                        color: !selectedPreset ? semanticColors.status.warning : semanticColors.text.tertiary,
                      }}
                      title={!selectedPreset ? "Click to return to default 'This Week' view" : "Reset to Week-to-Date"}
                    >
                      <ArrowPathIcon className={`size-5 transition-transform duration-300 group-hover:rotate-180 ${(shiftsLoading && clinicianShifts.length > 0) ? 'animate-spin' : ''}`} />
                      <div className="absolute inset-0 rounded-xl opacity-0 transition-opacity duration-300 group-hover:opacity-10" style={{ backgroundColor: semanticColors.status.info }} />
                    </button>
                    {!selectedPreset && (
                      <span className="text-xs text-amber-600 font-medium">
                        Custom dates selected
                      </span>
                    )}
                  </div>
                </div>

                {/* Quick Date Range Presets */}
                <div className="mb-6">
                  <label className="mb-3 block text-sm font-semibold" style={{ color: semanticColors.text.primary }}>
                    Quick Select
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {[
                      { 
                        label: 'Today', 
                        preset: 'today' as const,
                        tooltip: 'View shifts for today only'
                      },
                      { 
                        label: 'This Week', 
                        preset: 'thisWeek' as const,
                        tooltip: 'Default time range: Monday to today (week-to-date)'
                      },
                      { 
                        label: 'Last 7 Days', 
                        preset: 'last7Days' as const,
                        tooltip: 'View shifts from the last 7 days (inclusive)'
                      },
                      { 
                        label: 'This Month', 
                        preset: 'thisMonth' as const,
                        tooltip: 'View shifts for the current month'
                      },
                     
                    ].map((preset) => (
                      <div key={preset.label} className="relative group">
                        <button
                          onClick={() => handlePresetSelection(preset.preset)}
                          className={`relative px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60 ${
                            selectedPreset === preset.preset ? 'ring-2 ring-amber-400 ring-offset-2 shadow-lg' : 'hover:bg-gray-100'
                          }`}
                          style={{ 
                            backgroundColor: selectedPreset === preset.preset ? semanticColors.status.info + '20' : semanticColors.background.tertiary,
                            color: selectedPreset === preset.preset ? semanticColors.status.info : semanticColors.text.secondary,
                          }}
                          disabled={dateSelectionLoading}
                        >
                          {preset.label}
                          {selectedPreset === preset.preset && (
                            <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                              {preset.preset === 'thisWeek' ? '' : ''}
                            </span>
                          )}
                          <div className="absolute inset-0 rounded-xl opacity-0 transition-opacity duration-300 group-hover:opacity-10" style={{ backgroundColor: semanticColors.status.info }} />
                        </button>
                        
                        {/* Tooltip */}
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs font-medium text-white bg-gray-900 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10 shadow-lg">
                          {preset.tooltip}
                          {/* Tooltip arrow */}
                          <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Simple Date Range Selector */}
                <div className="mb-6">
                  {/* Simple Header */}
                  <div className="mb-4 flex items-center space-x-3">
                    <div className="flex size-6 items-center justify-center rounded-full" style={{ backgroundColor: semanticColors.status.info + '20' }}>
                      <svg className="size-3" style={{ color: semanticColors.status.info }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="text-base font-semibold" style={{ color: semanticColors.text.primary }}>
                      Select Date Range
                    </h3>
                    {(startDate && endDate) && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium" style={{ 
                        backgroundColor: selectedPreset ? semanticColors.status.success + '20' : semanticColors.status.warning + '20', 
                        color: selectedPreset ? semanticColors.status.success : semanticColors.status.warning 
                      }}>
                        {selectedPreset ? 'Range Set' : 'Custom Range'}
                      </span>
                    )}
                  </div>
                  
                  {/* Simple Date Input Field */}
                  <div className="relative">
                    <DatePicker
                      key={`range-${startDate?.getTime()}-${endDate?.getTime()}`}
                      selected={startDate}
                      onChange={handleDateChange}
                      selectsRange={true}
                      startDate={startDate}
                      endDate={endDate}
                      dateFormat="MMM dd, yyyy"
                      className="w-full rounded-xl border-2 pl-4 pr-4 py-3 text-base transition-all duration-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer hover:border-amber-400"
                      placeholderText="Click to select date range"
                      isClearable={true}
                      showYearDropdown={true}
                      showMonthDropdown={true}
                      dropdownMode="select"
                      disabled={dateSelectionLoading}
                      calendarClassName="custom-range-calendar"
                      minDate={new Date(2025, 0, 1)}
                      customInput={
                        <input
                          type="text"
                          className="w-full rounded-xl border-2 pl-4 pr-4 py-3 text-base transition-all duration-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer hover:border-amber-400"
                          placeholder="Click to select date range"
                          value={
                            startDate && endDate 
                              ? `${dayjs(startDate).format('MMM DD, YYYY')} - ${dayjs(endDate).format('MMM DD, YYYY')}`
                              : startDate 
                                ? `${dayjs(startDate).format('MMM DD, YYYY')} - Please select end date`
                                : 'Click to select date range'
                          }
                          readOnly
                          style={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '16px',
                            lineHeight: '1.5',
                            color: '#1f2937',
                            backgroundColor: 'white',
                            border: '2px solid #e5e7eb',
                            borderRadius: '12px',
                            width: '100%',
                            boxSizing: 'border-box',
                            textOverflow: 'visible',
                            overflow: 'visible',
                            whiteSpace: 'nowrap',
                            paddingRight: '20px',
                            minWidth: '400px'
                          }}
                        />
                      }
                    />
                    
                    {/* Message below input field */}
                    {startDate && !endDate && (
                      <div className="mt-2 flex items-center space-x-2 text-sm">
                        <div className="flex size-4 items-center justify-center rounded-full bg-amber-100">
                          <svg className="size-3 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <span className="text-amber-700 font-medium">
                          Please select another date to complete the range
                        </span>
                      </div>
                    )}
                  </div>
                </div>



                {/* Loading State */}
                {(shiftsLoading || dateSelectionLoading) && (
                  <div className="mt-4 animate-in fade-in-0 slide-in-from-bottom-2 duration-300">
                    <div className="flex items-center justify-center rounded-xl p-4" style={{ backgroundColor: semanticColors.background.tertiary }}>
                      <div className="flex items-center space-x-3">
                        <div className="size-6 animate-spin rounded-full border-2 border-amber-200 border-t-amber-500"></div>
                        <span className="text-sm font-medium" style={{ color: semanticColors.text.primary }}>
                          {dateSelectionLoading ? 'Processing date selection...' : 'Loading shifts for selected date range...'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Bottom Accent Line */}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 opacity-20" style={{
                background: `linear-gradient(90deg, transparent 0%, ${semanticColors.status.info} 50%, transparent 100%)`
              }} />
            </div>

            <div className="overflow-hidden rounded-xl border shadow-sm" style={{ backgroundColor: semanticColors.card.background, borderColor: semanticColors.card.border }}>
              {shiftsLoading && clinicianShifts.length === 0 && !shiftsApiError && (
                <div className="flex min-h-[200px] items-center justify-center p-6 text-center max-[768px]:min-h-[200px] max-[768px]:p-5 max-[640px]:min-h-[150px] max-[640px]:p-4 sm:min-h-[300px]">
                  <div className="mx-auto size-8 animate-spin rounded-full border-4 border-amber-500 border-t-transparent max-[768px]:size-7 max-[640px]:size-6"></div>
                  <p className="ml-3 text-sm font-medium text-gray-600 max-[768px]:text-sm max-[640px]:text-xs">Loading shifts...</p>
                </div>
              )}

              {!shiftsLoading && shiftsApiError && clinicianShifts.length === 0 && (
                <div className="flex min-h-[200px] flex-col items-center justify-center space-y-3 p-6 text-center max-[768px]:min-h-[200px] max-[768px]:p-5 max-[640px]:min-h-[150px] max-[640px]:p-4 sm:min-h-[300px]">
                  <ExclamationTriangleIcon className="size-10 text-red-400 max-[768px]:size-9 max-[640px]:size-8" />
                  <h3 className="text-base font-semibold text-gray-700 max-[768px]:text-base max-[640px]:text-sm sm:text-lg">Error Loading Shifts</h3>
                  <p className="max-w-sm text-xs text-gray-500 max-[768px]:text-xs max-[640px]:text-xs sm:text-sm">
                    {shiftsApiError}
                  </p>
                  <button
                    onClick={() => currentFacility?.facility_id && fetchClinicianShiftsCallback(currentFacility.facility_id)}
                    className="mt-2 rounded-md bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-amber-600 max-[768px]:px-2.5 max-[768px]:py-1 max-[768px]:text-xs max-[640px]:px-2 max-[640px]:py-1 max-[640px]:text-xs sm:text-sm"
                    disabled={!currentFacility?.facility_id || (shiftsLoading && clinicianShifts.length > 0)}
                  >
                    Try Again
                  </button>
                </div>
              )}

              {!shiftsLoading && !shiftsApiError && paginatedShifts.length === 0 && (
                <div className="flex min-h-[200px] flex-col items-center justify-center space-y-3 p-6 text-center max-[768px]:min-h-[200px] max-[768px]:p-5 max-[640px]:min-h-[150px] max-[640px]:p-4 sm:min-h-[300px]">
                  <NoSymbolIcon className="size-10 text-gray-400 max-[768px]:size-9 max-[640px]:size-8" />
                  <h3 className="text-base font-semibold text-gray-700 max-[768px]:text-base max-[640px]:text-sm sm:text-lg">No Shifts Found</h3>
                  <p className="max-w-sm text-xs text-gray-500 max-[768px]:text-xs max-[640px]:text-xs sm:text-sm">
                    There are no shifts matching your current filters. Try adjusting them or post a new shift.
                  </p>
                </div>
              )}

              {paginatedShifts.length > 0 && (
                <>
                  {/* Desktop Table View */}
                  <div className="hidden md:block overflow-hidden rounded-lg border" style={{ borderColor: semanticColors.card.border }}>
                    {/* Table Header - Compact Desktop */}
                    <div 
                      className="px-3 py-2 sm:px-4 sm:py-3 lg:px-3 lg:py-2"
                      style={{ backgroundColor: semanticColors.background.tertiary }}
                    >
                      <div className="grid grid-cols-5 gap-2 text-left text-xs font-semibold uppercase tracking-wider sm:gap-3 lg:gap-2" style={{ color: semanticColors.text.secondary }}>
                        <div>SCHEDULED TIME</div>
                        <div>POSITION</div>
                        <div>CLINICIAN NAME</div>
                        <div>RELIABILITY</div>
                        <div>STATUS</div>
                      </div>
                    </div>

                    {/* Table Body */}
                    <div className="divide-y" style={{ borderColor: semanticColors.card.border }}>
                      {paginatedShifts.map((shift) => {
                        const initials = shift.firstname && shift.lastname 
                          ? `${shift.firstname[0]}${shift.lastname[0]}`.toUpperCase()
                          : 'U';

                        return (
                          <div
                            key={shift.id}
                            className="px-3 py-2 transition-all duration-200 hover:bg-gray-50 sm:px-4 sm:py-3 lg:px-3 lg:py-2"
                          >
                            <div className="grid grid-cols-5 gap-2 items-center sm:gap-3 lg:gap-2">
                              {/* Scheduled Time Column */}
                              <div>
                                <div className="text-xs font-medium sm:text-sm lg:text-xs" style={{ color: semanticColors.text.primary }}>
                                  {formatTime(shift.shift_start_time, shift.shift_end_time)}
                                </div>
                                <div className="text-xs mt-1" style={{ color: semanticColors.text.secondary }}>
                                  {formatDate(shift.shift_start_time)}
                                </div>
                              </div>

                              {/* Position Column */}
                              <div>
                                <span className="inline-flex items-center rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 border border-blue-200">
                                  {shift.position_name}
                                </span>
                              </div>

                              {/* Clinician Name Column */}
                              <div 
                                className={`${shift.clinician_id ? 'cursor-pointer hover:underline' : ''}`}
                                onClick={() => shift.clinician_id && handleDetailsClick(shift)}
                              >
                                <div className="flex items-center space-x-2">
                                  <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-blue-200 text-xs font-semibold text-blue-700 sm:size-8 lg:size-6">
                                    {initials}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-semibold truncate sm:text-sm lg:text-xs" style={{ color: semanticColors.text.primary }}>
                                      {shift.firstname && shift.lastname 
                                        ? `${shift.firstname} ${shift.lastname}`
                                        : 'Unassigned'
                                      }
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Reliability Column */}
                              <div className="text-xs sm:text-sm lg:text-xs" style={{ color: semanticColors.text.primary }}>
                                N/A
                              </div>

                              {/* Status Column */}
                              <div>
                                <span 
                                  className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold"
                                  style={getStatusStyle(shift.status)}
                                >
                                  {getDisplayStatus(shift.status)}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Mobile/Tablet Card View */}
                  <div className="block space-y-4 md:hidden">
                    {paginatedShifts.map((shift) => {
                      const initials = shift.firstname && shift.lastname 
                        ? `${shift.firstname[0]}${shift.lastname[0]}`.toUpperCase()
                        : 'U';

                      return (
                        <div
                          key={shift.id}
                          className="rounded-lg border p-4 space-y-3"
                          style={{ 
                            backgroundColor: semanticColors.card.background,
                            borderColor: semanticColors.card.border 
                          }}
                        >
                          {/* Time and Position */}
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-sm" style={{ color: semanticColors.text.primary }}>
                                {formatTime(shift.shift_start_time, shift.shift_end_time)}
                              </div>
                              <div className="text-xs mt-1" style={{ color: semanticColors.text.secondary }}>
                                {formatDate(shift.shift_start_time)}
                              </div>
                            </div>
                            <span className="inline-flex items-center rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 border border-blue-200">
                              {shift.position_name}
                            </span>
                          </div>

                          {/* Clinician Info */}
                          <div 
                            className={`flex items-center space-x-3 ${shift.clinician_id ? 'cursor-pointer' : ''}`}
                            onClick={() => shift.clinician_id && handleDetailsClick(shift)}
                          >
                            <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-blue-200 text-sm font-semibold text-blue-700">
                              {initials}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold truncate" style={{ color: semanticColors.text.primary }}>
                                {shift.firstname && shift.lastname 
                                  ? `${shift.firstname} ${shift.lastname}`
                                  : 'Unassigned'
                                }
                              </div>
                              <div className="flex items-center space-x-2 mt-1">
                                <span 
                                  className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold"
                                  style={getStatusStyle(shift.status)}
                                >
                                  {getDisplayStatus(shift.status)}
                                </span>
                                <span className="text-xs" style={{ color: semanticColors.text.secondary }}>
                                  Reliability: N/A
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* View Details Button */}
                          <div className="flex justify-end">
                            <button
                              onClick={() => handleDetailsClick(shift)}
                              className="inline-flex items-center px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors duration-200"
                            >
                              <ChevronRightIcon className="w-3 h-3 mr-1" />
                              View Shift Details
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination Controls */}
                  <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 sm:px-6">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700">Rows per page:</span>
                        <div className="relative">
                          <select
                            value={rowsPerPage}
                            onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                            disabled={isRowsPerPageLoading}
                            className="rounded-md border-gray-300 bg-white px-2 py-1 text-sm text-gray-800 shadow-sm focus:border-amber-500 focus:ring-1 focus:ring-amber-500 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {[10, 20, 50].map(val => <option key={val} value={val}>{val}</option>)}
                          </select>
                          {isRowsPerPageLoading && (
                            <div className="absolute right-2 top-1/2 -translate-y-1/2">
                              <div className="size-3 animate-spin rounded-full border-2 border-gray-400 border-t-gray-700"></div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between sm:justify-center">
                        <span className="text-sm text-gray-700 sm:mr-4">
                          Page {pagination?.currentPage || currentPage} of {totalPages}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                            disabled={!pagination?.hasPreviousPage || isPageLoading}
                            className="rounded-md border border-gray-300 bg-white p-2 text-gray-500 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {isPageLoading ? (
                              <div className="size-4 animate-spin rounded-full border-2 border-gray-400 border-t-gray-700"></div>
                            ) : (
                              <ChevronLeftIcon className="size-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                            disabled={!pagination?.hasNextPage || isPageLoading}
                            className="rounded-md border border-gray-300 bg-white p-2 text-gray-500 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {isPageLoading ? (
                              <div className="size-4 animate-spin rounded-full border-2 border-gray-400 border-t-gray-700"></div>
                            ) : (
                              <ChevronRightIcon className="size-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </main>

       

        {selectedClinicianShift && (
          <ShiftModal
            open={modalOpen}
            handleClose={() => { setModalOpen(false); setSelectedClinicianShift(undefined); setClinicianProfile(null); setProfileError(null); }}
            shift={selectedClinicianShift}
            clinicianProfile={clinicianProfile}
            onUpdateShift={onUpdateShiftInList}
          />
        )}

        {profileError && (
          <div className="fixed bottom-4 right-4 z-50 max-[768px]:bottom-3 max-[768px]:right-3 max-[640px]:bottom-2 max-[640px]:right-2">
            <div className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-3 text-sm font-medium text-white shadow-lg max-[768px]:px-3.5 max-[768px]:py-2.5 max-[768px]:text-sm max-[640px]:px-3 max-[640px]:py-2 max-[640px]:text-xs">
              <ExclamationTriangleIcon className="size-5 max-[768px]:size-4 max-[640px]:size-4" />
              <span>{profileError}</span>
              <button onClick={() => setProfileError(null)} className="-mr-1 rounded-full p-1 hover:bg-red-700 max-[768px]:p-1 max-[640px]:p-0.5">
                <svg className="size-4 max-[768px]:size-3.5 max-[640px]:size-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};